import { Button } from '../ui/Button'
import { DefendSphereAiDispatchButtons } from './DefendSphereAiDispatchButtons'
import { DefendSphereAiMessage } from './DefendSphereAiMessage'
import { DefendSphereAiModeSelector } from './DefendSphereAiModeSelector'
import { DefendSphereAiOutboundPreview } from './DefendSphereAiOutboundPreview'
import { DefendSphereAiProviderStatus } from './DefendSphereAiProviderStatus'
import { formatDate, scopeLabel } from './conversationHelpers'
import { OPTIONAL_FILTERS } from './types'
import type { DefendSphereAiConversationApi } from './useDefendSphereAiConversation'
import './defendsphere-ai.css'

type TranslateFn = (key: string, fallback?: string) => string

const FILTER_FALLBACKS: Record<string, string> = {
  filterAll: 'All organization data (default)',
  filterEvidence: 'Evidence',
  filterPolicies: 'Policies',
  filterControls: 'Controls',
  filterStandards: 'Standards',
  filterAssets: 'Assets',
  filterInformationSystems: 'Information systems',
  filterVulnerabilities: 'Vulnerabilities',
  filterSuppliers: 'Suppliers',
  filterAuditReadiness: 'Audit readiness',
  filterIntegrations: 'Integrations',
  filterCertification: 'Certification',
  filterGovernanceIntelligence: 'Governance intelligence',
}

export function DefendSphereAiConversation({
  api,
  t,
  compact = false,
  showHistory = true,
}: {
  api: DefendSphereAiConversationApi
  t: TranslateFn
  compact?: boolean
  showHistory?: boolean
}) {
  return (
    <div className={`ds-ai-conversation ${compact ? 'ds-ai-conversation--compact' : ''}`}>
      <div className="ds-ai-conversation__toolbar">
        <Button type="button" size="small" color="primary" onClick={() => void api.createSession()}>
          {t('complianceCenterUi.convNew', 'New conversation')}
        </Button>
        <details className="ds-ai-filter">
          <summary>{t('complianceCenterUi.aiCiso.optionalFilter', 'Optional context filter')}</summary>
          <select
            className="ds-ai-input"
            value={api.optionalFilter}
            onChange={(e) => api.setOptionalFilter(e.target.value)}
            aria-label={t('complianceCenterUi.aiCiso.optionalFilter', 'Optional context filter')}
          >
            {OPTIONAL_FILTERS.map((filter) => (
              <option key={filter.value || 'all'} value={filter.value}>
                {t(
                  `complianceCenterUi.defendSphereAi.${filter.labelKey}`,
                  FILTER_FALLBACKS[filter.labelKey] || filter.labelKey,
                )}
              </option>
            ))}
          </select>
        </details>
        <label className="ds-ai-check ds-ai-check--toolbar">
          <input
            type="checkbox"
            checked={api.showArchived}
            onChange={(e) => api.setShowArchived(e.target.checked)}
          />
          {t('complianceCenterUi.convShowArchived', 'Show archived')}
        </label>
        <DefendSphereAiProviderStatus pending={api.pendingDisclosure} error={api.error} t={t} />
      </div>

      {api.error ? (
        <p className="ds-ai-error" role="alert">
          {api.error}
        </p>
      ) : null}

      {api.pendingDisclosure?.required ? (
        <DefendSphereAiOutboundPreview
          pending={api.pendingDisclosure}
          t={t}
          busy={api.disclosureBusy}
          editUserPrompt={api.editUserPrompt}
          editContextSummary={api.editContextSummary}
          editIncludePlatform={api.editIncludePlatform}
          editIncludeDocs={api.editIncludeDocs}
          editIncludeContext={api.editIncludeContext}
          showTransmitted={api.showTransmitted}
          onEditUserPrompt={api.setEditUserPrompt}
          onEditContextSummary={api.setEditContextSummary}
          onEditIncludePlatform={api.setEditIncludePlatform}
          onEditIncludeDocs={api.setEditIncludeDocs}
          onEditIncludeContext={api.setEditIncludeContext}
          onShowTransmitted={api.setShowTransmitted}
          onSaveEdits={() => void api.handleOutboundSaveEdits()}
          onApprove={(scope) => void api.handleDisclosureApprove(scope)}
          onDecline={() => void api.handleDisclosureDecline()}
          onDismiss={api.clearPendingDisclosure}
        />
      ) : null}

      <div className="ds-ai-conversation__layout">
        {showHistory ? (
          <aside className="ds-ai-history" aria-label={t('complianceCenterUi.convSessions', 'Conversations')}>
            <h3 className="ds-ai-history__title">{t('complianceCenterUi.convSessions', 'Conversations')}</h3>
            {api.loadingSessions ? <p className="ds-ai-muted">{t('common.loading', 'Loading…')}</p> : null}
            <ul className="ds-ai-history__list">
              {api.sessions.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={`ds-ai-history__item ${api.selectedId === s.id ? 'is-active' : ''}`}
                    onClick={() => api.setSelectedId(s.id)}
                  >
                    <div className="ds-ai-history__item-title">{s.title}</div>
                    <div className="ds-ai-muted">
                      {scopeLabel(s.contextScope)} · {s.messageCount} · {formatDate(s.lastInteractionAt)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            {!api.loadingSessions && !api.sessions.length ? (
              <p className="ds-ai-muted">{t('complianceCenterUi.convEmpty', 'No conversations yet.')}</p>
            ) : null}
          </aside>
        ) : null}

        <section className="ds-ai-main">
          {!api.selectedId ? (
            <div className="ds-ai-empty">
              <h3>{t('complianceCenterUi.defendSphereAi.productName', 'DefendSphere AI')}</h3>
              <p className="ds-ai-muted">
                {t(
                  'complianceCenterUi.defendSphereAi.defaultExperience',
                  'DefendSphere AI — AI CISO. Ask about risks, assets, vulnerabilities, evidence, controls, and suppliers.',
                )}
              </p>
              <Button type="button" size="small" color="primary" onClick={() => void api.createSession()}>
                {t('complianceCenterUi.convNew', 'New conversation')}
              </Button>
            </div>
          ) : (
            <>
              <div className="ds-ai-main__header">
                <div>
                  <div className="ds-ai-main__title">
                    {api.activeSession?.title ||
                      api.sessions.find((s) => s.id === api.selectedId)?.title ||
                      t('complianceCenterUi.convActive', 'Active conversation')}
                  </div>
                  <p className="ds-ai-muted">
                    {scopeLabel(
                      api.activeSession?.contextScope ||
                        api.sessions.find((s) => s.id === api.selectedId)?.contextScope ||
                        'AI_CISO',
                    )}
                    {api.activeSession?.conversationMode
                      ? ` · ${api.activeSession.conversationMode}`
                      : ''}
                    {api.activeSession?.preferredProviderKey
                      ? ` · ${api.activeSession.preferredProviderKey}`
                      : ''}
                    {api.activeSession?.preferredModelKey
                      ? ` / ${api.activeSession.preferredModelKey}`
                      : ''}
                  </p>
                  <DefendSphereAiModeSelector
                    runtime={api.modeRuntime || api.activeSession?.modeRuntime || null}
                    value={api.activeSession?.conversationMode}
                    busy={api.modeBusy}
                    disabled={api.sending || api.disclosureBusy}
                    onChange={(mode) => void api.updateConversationMode(mode)}
                    onGrantExternalConsent={() => api.grantExternalConsent()}
                    t={t}
                  />
                </div>
                <Button type="button" size="small" color="outline" onClick={() => void api.archiveSession()}>
                  {t('complianceCenterUi.convArchive', 'Archive')}
                </Button>
              </div>

              <div className="ds-ai-messages" role="log" aria-live="polite">
                {api.loadingDetail ? <p className="ds-ai-muted">{t('common.loading', 'Loading…')}</p> : null}
                {!api.loadingDetail && !api.messages.length ? (
                  <div className="ds-ai-empty ds-ai-empty--inline">
                    <p className="ds-ai-muted">
                      {t('complianceCenterUi.aiCiso.askPlaceholder', 'Ask your AI CISO...')}
                    </p>
                    {api.suggestedPrompts.length ? (
                      <div className="ds-ai-prompts">
                        {api.suggestedPrompts.slice(0, 6).map((prompt) => (
                          <button
                            key={prompt}
                            type="button"
                            className="ds-ai-prompt-chip"
                            onClick={() => void api.sendMessage(prompt)}
                            disabled={api.sending}
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {api.messages.map((msg) => (
                  <DefendSphereAiMessage key={msg.id} msg={msg} t={t} />
                ))}
              </div>

              <div className="ds-ai-composer">
                {api.messages.length && api.suggestedPrompts.length ? (
                  <div className="ds-ai-prompts ds-ai-prompts--composer">
                    {api.suggestedPrompts.slice(0, 4).map((prompt) => (
                      <button
                        key={prompt}
                        type="button"
                        className="ds-ai-prompt-chip"
                        onClick={() => void api.sendMessage(prompt)}
                        disabled={api.sending}
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                ) : null}
                <textarea
                  className="ds-ai-input"
                  value={api.draft}
                  onChange={(e) => api.setDraft(e.target.value)}
                  placeholder={t('complianceCenterUi.aiCiso.askPlaceholder', 'Ask your AI CISO...')}
                  disabled={api.sending}
                  aria-label={t('complianceCenterUi.aiCiso.askPlaceholder', 'Ask your AI CISO...')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && api.draft.trim() && !api.sending) {
                      e.preventDefault()
                      // Ctrl/Cmd+Enter uses the selected session mode (no per-message mode override)
                      const mode = String(
                        api.activeSession?.conversationMode || api.modeRuntime?.conversationMode || '',
                      ).toUpperCase()
                      void api.sendMessage(
                        api.draft,
                        mode === 'EXTERNAL_AI_RAG' ? { dispatchTarget: 'EXTERNAL_AI_RAG' } : undefined,
                      )
                    }
                  }}
                />
                <DefendSphereAiDispatchButtons
                  runtime={api.modeRuntime || api.activeSession?.modeRuntime || null}
                  draft={api.draft}
                  sending={api.sending}
                  disclosureBusy={api.disclosureBusy}
                  modeBusy={api.modeBusy}
                  selectedMode={api.activeSession?.conversationMode || api.modeRuntime?.conversationMode}
                  onDispatch={(target) => void api.sendMessage(api.draft, { dispatchTarget: target })}
                  t={t}
                />
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  )
}
